import { DaysService } from "../../timeline/daily-overview/days.service";
import { CreatedMood, CreatedMoods, MoodsService } from "../../timeline/timeline-item/moods/moods.service";
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { LoggerService } from '@teammood/shared/util/logger';
import { AddFeedbackDialogService } from '../add-feedback.service';
import { filter, from, mergeMap, Subject, takeUntil, tap } from 'rxjs';
import { Mood } from "@teammood/timeline";
import { ActivatedRoute, Router } from "@angular/router";

@Component({
    selector: 'add-comment',
    templateUrl: './add-comment.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddCommentComponent implements OnInit, OnDestroy {
    
    mood_id: string;
    teamNavigation: { currentMoodId: string; currentTeamIndex: number; currentTeamId: string; previousComment: string; };
    severalTeams: boolean;
    moods: Array<MoodWithTeamName>;
    moods_not_saved_modal: any;

    private _destroy$ = new Subject<void>();

    constructor(
        private log: LoggerService,
        private moodsService: MoodsService,
        private daysService: DaysService,
        private addFeedbackDialogService: AddFeedbackDialogService,
        private route: ActivatedRoute,
        private router: Router,
    ) {}
    
    async ngOnInit() {

        this.route.params
            .pipe(
                filter(params => !!params['moodId']),
                mergeMap(async params => {
                    const moodId = params['moodId']
                    this.mood_id = moodId;

                    this.teamNavigation = {
                        currentMoodId: moodId,
                        currentTeamIndex: undefined,
                        currentTeamId: undefined,
                        previousComment: undefined
                    }

                    if (moodId) {
                        this.moods = await this.load_moods(moodId);
                    } else{
                        return this.router.navigate(['/login']);
                    }
                }),
                takeUntil(this._destroy$)
            ).subscribe();


    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    private async load_moods (moodId: string): Promise<MoodWithTeamName[]> {
        try {
            const moodData: CreatedMoods = await this.moodsService.getMoods(moodId);
            const moods = this.sortMoods(moodData.moods);

            if (moods.length > 0) {

                // for prod debugging, to remove after a while
                if (moods.filter(mood => mood.id === this.mood_id)[0] === undefined) {
                    this.log.error('Issue with team id for moods as moods[0] is undefined : ' + moodData)
                }

                this.teamNavigation.currentTeamId = moods.filter(mood => mood.id === this.mood_id)[0].teamId;
                this.teamNavigation.currentTeamIndex = moods.findIndex(mood => mood.teamId === this.teamNavigation.currentTeamId);
                this.severalTeams = moods.length > 1;
            }

            return moods.map(createdMood => {

                return new MoodWithTeamName(createdMood);
            });

        } catch(error) {
            await this.router.navigate(['/login'])
        }
    }

    private async navigateNext(){
        if(this.teamNavigation.currentTeamIndex === this.moods.length - 1){
            // go to add other feeback
            await this.router.navigate(['add_other_feedback', this.mood_id]);
        }else{
            // go to next team
            this.teamNavigation.currentTeamIndex++;
            const nextMoodId = this.moods[this.teamNavigation.currentTeamIndex].id;
            await this.router.navigate(['/add_comment', nextMoodId]);
        }
    }

    async onSaveCommentDialog(currentMood, action) {
        const dialogRef = await this.addFeedbackDialogService.openSaveCommentDialog(currentMood);

        dialogRef.beforeClosed().pipe(
            tap(async mood => {
                if (mood) {
                    await this.daysService.updateMood(mood);
                }
                switch (action) {
                    case 'next':
                        this.navigateNext();
                        break;
                    case 'skip':
                        await this.router.navigate(['add_other_feedback']);
                        break;
                }
            }),
            takeUntil(dialogRef.afterClosed())
        ).subscribe();
    }

    next () {
        const currentMood = this.moods[this.teamNavigation.currentTeamIndex];
        if (!currentMood.commentSaved) {
            this.onSaveCommentDialog(currentMood, 'next')
        } else {
            this.navigateNext();
        }
    }

    async skip (){
        const currentMood = this.moods[this.teamNavigation.currentTeamIndex];
        if (!currentMood.commentSaved) {
            await this.onSaveCommentDialog(currentMood, 'skip')
        } else {
            await this.router.navigate(['add_other_feedback', currentMood.id]);
        }
    }

    async previous (){
        this.teamNavigation.currentTeamIndex--;
        const previousMoodId = this.moods[this.teamNavigation.currentTeamIndex].id;
        await this.router.navigate(['/add_comment', previousMoodId]);
    }

    private sortMoods(moods: CreatedMood[]){
        return moods.sort((a,b) => a.id > b.id ? 1 : -1)
    }
}

class MoodWithTeamName extends Mood {
    teamName: string;

    constructor(createdMood: CreatedMood) {
        super();
        this.teamName = createdMood.teamName;
        this.commentSaved = true;
        this.mood = createdMood.mood;
        this.id = createdMood.id;
        this.teamId = createdMood.teamId;
    }
}